import { cn } from '@pubstack/ui/utils/cn'
import { useDebouncedCallback } from '@mantine/hooks'
import {
  BidParams,
  BidParamsMapping,
  BidParamsMappingDimensions,
  CreateBidParamsMapping,
  MAPPING_DIMENSIONS,
  MappingDimensionLabel,
  MappingDimensionsConfig,
} from '@pubstack/common/src/bid-params-mapping'
import { BidderSeat } from '@pubstack/common/src/bidder-seat'
import { BidParamSpec } from '@pubstack/common/src/bidderCatalog'
import { parseBooleanBidParam } from '@pubstack/common/src/bidderParamValidation'
import { MediaType } from '@pubstack/common/src/media-types'
import { compareNames } from '@pubstack/common/src/sort'
import { isEqual } from 'lodash-es'
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import { CellBase, CellComponentProps, Matrix, Spreadsheet } from 'react-spreadsheet'
import { Header, NoDataSpreadsheet, RowIndicator, SpreadsheetContainer } from '~/components/_styled/spreadsheet'
import { Icon } from '~/components/Icon'
import { Tooltip } from '~/components/Tooltip'
import { SpreadsheetCell } from '~/feature-components/SpreadsheetCell'

// Constants
const HEADER_WIDTHS: { label: MappingDimensionLabel; width: number }[] = [
  {
    label: 'Site name',
    width: 260,
  },
  {
    label: 'Ad unit name',
    width: 260,
  },
  {
    label: 'Placement',
    width: 180,
  },
  {
    label: 'Media type',
    width: 130,
  },
]

const buildRowIdFromDimensions = (dimensions: BidParamsMappingDimensions) => Object.values(dimensions).join('-')

// Sort on the *displayed* label, mirroring the cell rendering (`labels[value] || ''`): when a label is
// missing the cell shows an empty string, so sorting must too — never fall back to the raw dimension id.
const getDimensionSortLabel = (dimensions: BidParamsMappingDimensions, key: keyof BidParamsMappingDimensions, config: MappingDimensionsConfig | undefined): string => {
  const value = dimensions[key]
  if (value === undefined) return ''
  return config?.[key]?.labels?.[value] ?? ''
}

type SpreadsheetDataType = {
  rowId: string
  value: (string | number | boolean | unknown[] | BidParams) | (MediaType | undefined)
  readOnly: boolean
}

/**
 * The bid params of an edited row: what the grid holds now, on top of the values it shows no column for.
 *
 * Callers choose which specs to display, and they do not all choose the same ones. The ad units view left
 * the seat's `ext.*` keys out, so rebuilding `bidParams` from the columns alone deleted those values as
 * soon as any cell of the row was edited.
 *
 * A cleared cell still removes its param: its column is editable, so it is not carried over.
 */
export const mergeEditedBidParams = (previous: BidParams, edited: BidParams, editableParamNames: Set<string>): BidParams => ({
  ...Object.fromEntries(Object.entries(previous).filter(([name]) => !editableParamNames.has(name))),
  ...edited,
})

type ValidatedMapping = {
  rowId: string
  mapping: BidParamsMapping | CreateBidParamsMapping
  errors: { cellIndex: number; message: string }[]
}

const validateBidParams = (mappings: (BidParamsMapping | CreateBidParamsMapping)[], bidParamSpecs: BidParamSpec[], readOnlyParams: Set<string>): ValidatedMapping[] => {
  return mappings.map((mapping: BidParamsMapping | CreateBidParamsMapping) => {
    const rowId = (mapping as BidParamsMapping).id || buildRowIdFromDimensions(mapping.dimensions)
    const validatedMapping: ValidatedMapping = {
      rowId,
      mapping,
      errors: [],
    }

    if (Object.entries(mapping.bidParams).length === 0) return validatedMapping

    for (let i = 0; i < bidParamSpecs.length; i++) {
      const bidParamSpec = bidParamSpecs[i]
      const paramSpecName = bidParamSpec.name
      // A read-only column cannot be wrong, and it holds no value to check.
      if (readOnlyParams.has(paramSpecName)) continue
      const bidParamValue = mapping.bidParams[paramSpecName]

      if (!bidParamSpec) return validatedMapping

      if (bidParamSpec.required && !bidParamValue)
        validatedMapping.errors.push({
          cellIndex: i,
          message: `Missing value for ${bidParamSpec.name}`,
        })

      if (!bidParamValue) continue

      const typeError = {
        cellIndex: i,
        message: `${bidParamSpec.name} is expected to be of ${bidParamSpec.type} type`,
      }

      switch (bidParamSpec.type) {
        case 'string': {
          break
        }
        case 'number': {
          const numValue = parseInt(bidParamValue as string)
          if (isNaN(numValue)) {
            validatedMapping.errors.push(typeError)
          }
          break
        }
        case 'array': {
          if (typeof bidParamValue !== 'string') {
            validatedMapping.errors.push(typeError)
          }
          if (!(bidParamValue as string).match(/^\[\s*'(?:[^'\\]|\\.)*'(\s*,\s*'(?:[^'\\]|\\.)*')*\s*\]$/)) {
            validatedMapping.errors.push(typeError)
          }
          break
        }
        case 'boolean': {
          // Same tokens as the backend, so a value accepted on upload is not rejected in the grid.
          if (parseBooleanBidParam(bidParamValue as string) === undefined) {
            validatedMapping.errors.push(typeError)
          }
          break
        }
      }
    }
    return validatedMapping
  })
}

const Cell: FunctionComponent<CellComponentProps<CellBase & SpreadsheetDataType> & { errorMessage?: string }> = ({ errorMessage, ...restProps }) => {
  return <SpreadsheetCell {...restProps} errorMessage={errorMessage} />
}

type Props = {
  bidderSeat: BidderSeat
  mappingDimensionsConfig: MappingDimensionsConfig | undefined
  hiddenColumns?: (keyof BidParamsMappingDimensions)[]
  bidParamSpecs: BidParamSpec[]
  /**
   * Columns shown but not editable, by param name. An `object` param with no keys declared yet appears
   * this way: the column says the bidder takes custom keys, and the seat settings are where to add them.
   */
  readOnlyParamNames?: string[]
  /** Opens the seat settings from such a column's header. Without it, the header only explains itself. */
  onConfigureParam?: () => void
  mappings: (BidParamsMapping | CreateBidParamsMapping)[]
  readOnly?: boolean
  onDraftMappingsChange?: (draftMappings: (BidParamsMapping | CreateBidParamsMapping)[]) => void
  onErroredMappingsChange?: (e: boolean) => void
  showBidderInSpreadsheet?: boolean
  /** Grows the table into whatever vertical space its container gives it, instead of capping at 450px — for hosts (like the seat quick-edit flyout) that already bound that space themselves. */
  fillHeight?: boolean
}

export const MappingsSpreadsheet: FunctionComponent<Props> = ({
  bidderSeat,
  mappingDimensionsConfig,
  hiddenColumns,
  bidParamSpecs: bidParamSpecsProp,
  readOnlyParamNames,
  onConfigureParam,
  mappings,
  readOnly = false,
  onDraftMappingsChange,
  onErroredMappingsChange,
  showBidderInSpreadsheet = false,
  fillHeight = false,
}) => {
  // Catalog params can be platform-specific: keep those with no platform restriction, or matching the bidder seat's
  // platform (defaulting to 'server' as the schema does — some seats have no stored platform, which would otherwise drop every tagged param).
  const bidParamSpecs = useMemo(() => bidParamSpecsProp.filter((spec) => !spec.platform?.length || spec.platform.includes(bidderSeat.platform ?? 'server')), [bidParamSpecsProp, bidderSeat.platform])

  // State
  const [localMappings, setLocalMappings] = useState<(BidParamsMapping | CreateBidParamsMapping)[]>(mappings)
  // Active dimension column sort. When null, rows keep their natural order.
  const [sortState, setSortState] = useState<{ key: keyof BidParamsMappingDimensions; direction: 'asc' | 'desc' } | null>(null)

  // Dimension keys in the same order as their spreadsheet columns (after the optional Bidder column),
  // so a clicked header column can be mapped back to the dimension it sorts on.
  const sortableDimensionKeys = useMemo<(keyof BidParamsMappingDimensions)[]>(
    () =>
      mappingDimensionsConfig ? (Object.keys(mappingDimensionsConfig) as (keyof BidParamsMappingDimensions)[]).filter((k) => !hiddenColumns?.includes(k) && MAPPING_DIMENSIONS[k] !== undefined) : [],
    [mappingDimensionsConfig, hiddenColumns]
  )

  const handleDimensionSort = useCallback((key: keyof BidParamsMappingDimensions) => {
    setSortState((prev) => {
      if (prev?.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }, [])

  const draftMappings = useMemo(
    () =>
      localMappings.filter((m1) => {
        const savedMapping = mappings.find((m2) => buildRowIdFromDimensions(m1.dimensions) === buildRowIdFromDimensions(m2.dimensions))
        if (!savedMapping) return true
        return !isEqual(savedMapping.bidParams, m1.bidParams)
      }),
    [localMappings, mappings]
  )

  const readOnlyParams = useMemo(() => new Set(readOnlyParamNames ?? []), [readOnlyParamNames])

  const validatedMappings = useMemo<ValidatedMapping[]>(() => validateBidParams(localMappings, bidParamSpecs, readOnlyParams), [localMappings, bidParamSpecs, readOnlyParams])

  const spreadsheetData: Matrix<SpreadsheetDataType> = useMemo(() => {
    // The ad unit name is always used as a sort key: as the default order when no header is active, and
    // as a secondary (tie-breaking) sort when another dimension is active so rows sharing the same primary
    // value stay grouped by ad unit; it stays ascending regardless of the primary sort direction.
    const compareByAdUnit = (a: BidParamsMapping | CreateBidParamsMapping, b: BidParamsMapping | CreateBidParamsMapping) =>
      compareNames(getDimensionSortLabel(a.dimensions, 'adUnitId', mappingDimensionsConfig), getDimensionSortLabel(b.dimensions, 'adUnitId', mappingDimensionsConfig))

    const orderedMappings = [...localMappings].sort((a, b) => {
      if (!sortState) return compareByAdUnit(a, b)
      const cmp = compareNames(getDimensionSortLabel(a.dimensions, sortState.key, mappingDimensionsConfig), getDimensionSortLabel(b.dimensions, sortState.key, mappingDimensionsConfig))
      const primary = sortState.direction === 'asc' ? cmp : -cmp
      return primary !== 0 || sortState.key === 'adUnitId' ? primary : compareByAdUnit(a, b)
    })

    const data = mappingDimensionsConfig
      ? orderedMappings.map((e) => {
          const row = []
          const rowId = (e as BidParamsMapping).id || buildRowIdFromDimensions(e.dimensions)
          if (showBidderInSpreadsheet) {
            row.push({ value: bidderSeat.name, readOnly: true, rowId })
          }
          Object.keys(mappingDimensionsConfig).forEach((prop) => {
            const dimensionKey = prop as keyof BidParamsMappingDimensions
            if (hiddenColumns?.includes(dimensionKey) || !mappingDimensionsConfig[dimensionKey] || !e.dimensions[dimensionKey] || !mappingDimensionsConfig[dimensionKey].labels) return
            row.push({ value: mappingDimensionsConfig[dimensionKey].labels[e.dimensions[dimensionKey]] || '', readOnly: true, rowId })
          })
          row.push(
            ...bidParamSpecs.map((bps) => {
              const param = e.bidParams[bps.name] || ''
              return {
                value: param,
                readOnly: readOnly || readOnlyParams.has(bps.name),
                rowId,
              }
            })
          )
          return row
        })
      : []

    return data
  }, [localMappings, mappingDimensionsConfig, hiddenColumns, bidParamSpecs, readOnly, readOnlyParams, showBidderInSpreadsheet, bidderSeat, sortState])

  const bidParamsHeaders = bidParamSpecs.map((param) => param.name)
  const dimensionHeaders: MappingDimensionLabel[] = [
    ...(showBidderInSpreadsheet ? ['Bidder' as MappingDimensionLabel] : []),
    ...(mappingDimensionsConfig
      ? (Object.keys(mappingDimensionsConfig).filter((k) => !hiddenColumns?.find((hc) => hc === k)) || [])
          .map((k) => {
            return MAPPING_DIMENSIONS[k as keyof BidParamsMappingDimensions]
          })
          .filter((e): e is MappingDimensionLabel => e !== undefined)
      : []),
  ]
  const headers = useMemo(() => [...dimensionHeaders, ...bidParamsHeaders], [dimensionHeaders, bidParamsHeaders])

  /** The params this grid can write. An edit rebuilds only these, and leaves every other one alone. */
  const editableParamNames = useMemo(() => new Set(bidParamsHeaders.filter((name) => !readOnlyParams.has(name))), [bidParamsHeaders, readOnlyParams])

  const handleChange = useCallback(
    (data: Matrix<SpreadsheetDataType>) => {
      const newMappings = [...localMappings]

      data.forEach((row, index) => {
        const rowIndex = localMappings.findIndex((e) => {
          const rowId = (e as BidParamsMapping).id || buildRowIdFromDimensions(e.dimensions)
          return rowId === row[0]?.rowId
        })
        const newBidParams = Object.fromEntries(
          headers
            .map((header, hIndex) => (dimensionHeaders.some((dh) => dh === header) || readOnlyParams.has(header) ? [] : [header, data[index][hIndex]?.value ?? '']))
            .filter((entry) => entry.length && !!entry[1])
        )
        if (rowIndex >= 0) {
          const previous = localMappings[rowIndex]
          newMappings.splice(rowIndex, 1, {
            ...previous,
            bidParams: mergeEditedBidParams(previous.bidParams, newBidParams, editableParamNames),
          })
        }
      })
      setLocalMappings(newMappings)
    },
    [headers, dimensionHeaders, localMappings, readOnlyParams, editableParamNames]
  )

  const debouncedHandleChange = useDebouncedCallback(handleChange, 100)

  useEffect(() => {
    // TODO jba bvi 2025-05-16: react-spreadsheet seems to have a bug where the `onChange` method is triggered when the input data of the spreadsheet is changing
    // Setting a timeout to avoid failing at refreshing the mapping data
    setTimeout(() => setLocalMappings(mappings), 0)
  }, [mappings])

  // Emit draft mappings to parent when they change
  useEffect(() => {
    onDraftMappingsChange?.(draftMappings)
  }, [draftMappings, onDraftMappingsChange])

  // Emit draft mappings to parent when they change
  useEffect(() => {
    onErroredMappingsChange?.(validatedMappings.some((e) => e.errors.length > 0))
  }, [validatedMappings, onErroredMappingsChange])

  return (
    <SpreadsheetContainer $fillHeight={fillHeight}>
      {spreadsheetData.length > 0 ? (
        <>
          <Spreadsheet
            ColumnIndicator={({ column, onSelect, selected, label }) => {
              const spec = bidParamSpecs[column - dimensionHeaders.length]
              const isRequired = spec?.required
              // This column exists only to say the bidder takes custom keys, so it carries both the
              // explanation and the link. A banner above the table would repeat it to everyone, every time.
              const isCustomizable = !!spec && readOnlyParams.has(spec.name)
              const dimensionIndex = column - (showBidderInSpreadsheet ? 1 : 0)
              const dimensionKey = dimensionIndex >= 0 && dimensionIndex < sortableDimensionKeys.length ? sortableDimensionKeys[dimensionIndex] : undefined
              const sortDirection = dimensionKey && sortState?.key === dimensionKey ? sortState.direction : undefined
              return (
                <Header
                  fixedWidth={HEADER_WIDTHS.find((e) => e.label === headers[column])?.width}
                  onClick={(event) => (dimensionKey ? handleDimensionSort(dimensionKey) : onSelect(column, event.shiftKey))}
                  className={cn(['Spreadsheet__header', selected && 'Spreadsheet__header--selected'])}
                >
                  {isCustomizable ? (
                    <Tooltip
                      title={`This bidder takes the custom keys you declare under ${spec.name}. Each key becomes a column of this mapping, filled in per ad unit.`}
                      positions={['bottom', 'top']}
                      align="end"
                      reposition
                    >
                      <span className="inline-flex items-center gap-1">
                        {label}
                        {onConfigureParam && (
                          <button
                            type="button"
                            aria-label={`Add ${spec.name} keys in seat settings`}
                            className="inline-flex cursor-pointer border-0 bg-transparent p-0 text-[var(--color-hurricane)] hover:text-(--color-king)"
                            onClick={(event) => {
                              // The header click also selects the column, which would hide the flyout behind the selection.
                              event.stopPropagation()
                              onConfigureParam()
                            }}
                          >
                            <Icon name="settings" />
                          </button>
                        )}
                      </span>
                    </Tooltip>
                  ) : (
                    <>
                      {label} {isRequired && '*'}
                    </>
                  )}
                  {sortDirection && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                </Header>
              )
            }}
            Cell={(props) => {
              const errorMessage = validatedMappings?.find((e) => e?.rowId === props.data?.rowId)?.errors.find((e) => dimensionHeaders.length + (e?.cellIndex || 0) === props.column)?.message
              return <Cell {...props} errorMessage={errorMessage} />
            }}
            RowIndicator={({ onSelect, row, selected, label }) => {
              const rowId = spreadsheetData[row]?.[0]?.rowId
              const hasError = validatedMappings?.some((e) => {
                if (rowId === e?.rowId) return e.errors.length > 0
              })
              return (
                <RowIndicator
                  hasError={hasError}
                  selected={selected}
                  onClick={(event) => onSelect(row, event.shiftKey)}
                  className={cn(['Spreadsheet__header', selected && 'Spreadsheet__header--selected'])}
                >
                  {label ?? row}
                </RowIndicator>
              )
            }}
            css={fillHeight ? { overflowY: 'auto', overflowX: 'auto', flex: 1, minHeight: 0 } : { overflowY: 'auto', overflowX: 'auto', maxHeight: '440px' }}
            data={spreadsheetData}
            columnLabels={headers}
            onChange={(data) => debouncedHandleChange(data)}
            // Needs to be set to an empty function to avoid performance issues
            // we don't use the formula parser in this context
            createFormulaParser={() => () => {}}
          />
        </>
      ) : (
        <NoDataSpreadsheet $fillHeight={fillHeight}>
          <Icon name="night_sky" width="144px" />
          <div>No mapping found</div>
        </NoDataSpreadsheet>
      )}
    </SpreadsheetContainer>
  )
}
